import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import Card from "@mui/material/Card"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import TablePagination from "@mui/material/TablePagination"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import MenuItem from "@mui/material/MenuItem"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import ListItemIcon from "@mui/material/ListItemIcon"
import ListItemText from "@mui/material/ListItemText"
import { Iconify } from "@/src/common/iconify"
import { DashboardContent } from "@/src/layouts/dashboard"
import { paths } from "@/src/routes/paths"
import { DeleteLeadsModal } from "@/src/features/internalUsers/modals/DeleteLeadsModal"
import { AssignRecruitersToJobModal } from "@/src/features/internalUsers/modals/AssignRecruitersToJobModal"
import { useInternalStore } from "@/src/store/slices/internal.slice"
import type { InternalJobApi } from "@/src/services/internal"
import { getJobStatusPillSx } from "@/src/features/internalUsers/constants"

// ----------------------------------------------------------------------

export default function JobsAndLeads() {
    const navigate = useNavigate()
    const { workspaces, jobs, jobsLoading, jobsError, jobsPagination, fetchWorkspaces, fetchJobs, setSelectedJob } =
        useInternalStore()
    const [workspaceFilter, setWorkspaceFilter] = useState<string>("all")
    const [search, setSearch] = useState("")
    const [debouncedSearch, setDebouncedSearch] = useState("")
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [menuJob, setMenuJob] = useState<InternalJobApi | null>(null)
    const [deleteLeadsModalOpen, setDeleteLeadsModalOpen] = useState(false)
    const [assignRecruitersModalOpen, setAssignRecruitersModalOpen] = useState(false)

    useEffect(() => {
        fetchWorkspaces()
    }, [fetchWorkspaces])

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400)
        return () => clearTimeout(t)
    }, [search])

    const jobsParams = {
        page: 0,
        size: 20,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(workspaceFilter !== "all" && { workspaceId: workspaceFilter }),
    }

    useEffect(() => {
        fetchJobs({
            page: 0,
            size: 20,
            ...(debouncedSearch && { search: debouncedSearch }),
            ...(workspaceFilter !== "all" && { workspaceId: workspaceFilter }),
        })
    }, [fetchJobs, debouncedSearch, workspaceFilter])

    const handlePageChange = (_: unknown, newPage: number) => {
        fetchJobs({ ...jobsParams, page: newPage, size: jobsPagination.pageSize })
    }

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSize = Number(e.target.value) || 20
        fetchJobs({ ...jobsParams, page: 0, size: newSize })
    }

    const workspaceOptions = [{ id: "all", name: "All Workspaces" }, ...workspaces]

    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>, row: InternalJobApi) => {
        e.stopPropagation()
        setMenuAnchor(e.currentTarget)
        setMenuJob(row)
    }

    const handleCloseMenu = () => {
        setMenuAnchor(null)
    }

    const handleDeleteLeadsClick = () => {
        setDeleteLeadsModalOpen(true)
        setMenuAnchor(null)
    }

    const handleAssignRecruitersClick = () => {
        setAssignRecruitersModalOpen(true)
        setMenuAnchor(null)
    }

    const handleDeleteLeadsModalClose = () => {
        setDeleteLeadsModalOpen(false)
        setMenuJob(null)
    }

    const handleAssignRecruitersModalClose = () => {
        setAssignRecruitersModalOpen(false)
        setMenuJob(null)
    }

    const refreshJobsList = () => {
        fetchJobs({
            page: jobsPagination.page,
            size: jobsPagination.pageSize,
            ...(debouncedSearch && { search: debouncedSearch }),
            ...(workspaceFilter !== "all" && { workspaceId: workspaceFilter }),
        })
    }

    const handleDeleteLeadsSuccess = () => {
        setDeleteLeadsModalOpen(false)
        setMenuJob(null)
        refreshJobsList()
    }

    const handleAssignRecruitersSuccess = () => {
        setAssignRecruitersModalOpen(false)
        setMenuJob(null)
        refreshJobsList()
    }

    return (
        <DashboardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", paddingBottom: "20px", mt: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>
                        Jobs & Leads
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Manage jobs, expiry, and lead-level controls.
                    </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <TextField
                        placeholder="Search by job name"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ minWidth: 240 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" width={20} sx={{ color: "text.disabled" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <TextField
                        select
                        value={workspaceFilter}
                        onChange={(e) => setWorkspaceFilter(e.target.value)}
                        size="small"
                        sx={{ minWidth: 180 }}
                    >
                        {workspaceOptions.map((w) => (
                            <MenuItem key={w.id} value={w.id}>
                                {w.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Stack>

            {jobsError && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                    {jobsError}
                </Typography>
            )}

            <Card sx={{ overflow: "hidden", border: 1, borderColor: "divider", borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: "grey.50" }}>
                                <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>Job Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                    Workspace
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>Leads</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: "text.secondary", py: 2 }}>
                                    Assigned recruiters
                                </TableCell>
                                <TableCell
                                    align="right"
                                    sx={{ fontWeight: 600, color: "text.secondary", py: 2, width: 56 }}
                                />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={32} />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Loading jobs...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                jobs.map((row) => (
                                    <TableRow
                                        key={row.jobId}
                                        hover
                                        onClick={() => {
                                            setSelectedJob(row)
                                            navigate(paths.internalUser.jobLeadsDetail(row.jobId))
                                        }}
                                        sx={{
                                            cursor: "pointer",
                                            "&:last-child td": { borderBottom: 0 },
                                            "& td": { py: 2, borderBottom: 1, borderColor: "divider" },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500} color="primary.main">
                                                {row.jobName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.workspaceName ?? row.workspaceId ?? "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: "inline-block",
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: 10,
                                                    typography: "caption",
                                                    fontWeight: 600,
                                                    ...getJobStatusPillSx(row.status),
                                                }}
                                            >
                                                {row.status ?? "—"}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.leadsCount ?? 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.assigneeCount ?? 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                            <IconButton
                                                size="small"
                                                sx={{ color: "text.secondary" }}
                                                aria-label="Actions"
                                                onClick={(e) => handleOpenMenu(e, row)}
                                            >
                                                <Iconify icon="eva:more-vertical-fill" width={22} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={jobsPagination.totalElements}
                    page={jobsPagination.page}
                    onPageChange={handlePageChange}
                    rowsPerPage={jobsPagination.pageSize}
                    rowsPerPageOptions={[10, 20, 40, 100]}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    sx={{ borderTop: 1, borderColor: "divider" }}
                />
            </Card>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{ paper: { sx: { minWidth: 160 } } }}
            >
                <MenuItem onClick={handleAssignRecruitersClick}>
                    <ListItemIcon>
                        <Iconify icon="mdi:account-plus-outline" width={22} />
                    </ListItemIcon>
                    <ListItemText>Assign recruiters</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDeleteLeadsClick}>
                    <ListItemIcon>
                        <Iconify icon="mdi:link-variant-off" width={22} />
                    </ListItemIcon>
                    <ListItemText>Delete leads</ListItemText>
                </MenuItem>
            </Menu>

            <AssignRecruitersToJobModal
                open={assignRecruitersModalOpen}
                onClose={handleAssignRecruitersModalClose}
                jobName={menuJob?.jobName ?? ""}
                jobId={menuJob?.jobId}
                workspaceId={menuJob?.workspaceId}
                onSuccess={handleAssignRecruitersSuccess}
            />
            <DeleteLeadsModal
                open={deleteLeadsModalOpen}
                onClose={handleDeleteLeadsModalClose}
                jobName={menuJob?.jobName ?? ""}
                jobId={menuJob?.jobId}
                workspaceId={menuJob?.workspaceId}
                onSuccess={handleDeleteLeadsSuccess}
            />
        </DashboardContent>
    )
}
